import type {
  DragEvent as ReactDragEvent,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  PointerEvent as ReactPointerEvent,
  RefObject,
  TouchEvent as ReactTouchEvent,
} from 'react'
import type { ChecklistItem, ChecklistMemberOption } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import {
  GATE_DIAMETER,
  NODE_BODY_HEIGHT,
  NODE_WIDTH,
} from '../../services/canvasLayoutConstants'
import { isGateNode } from './boardItem'
import { isTimerExpired } from './canvasMisc'
import { GateNode } from '../ChecklistBoard/GateNode'
import { BoardNode } from '../ChecklistBoard/BoardNode'
import { VectorGestureKnife, type SliceableWire } from '../DrawingBoard/VectorGestureKnife'

export interface BoardViewportProps {
  boardRef: RefObject<HTMLDivElement | null>
  isPlacingNode: boolean
  viewportSize: { width: number; height: number }
  handleBoardPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void
  handlePointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void
  handlePointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void
  syncCanvasTransformFromScroll: () => void
  canEditStructure: boolean
  handleCalendarDrop: (event: ReactDragEvent<HTMLDivElement>) => void
  boardMetrics: { width: number; height: number }
  boardScale: number
  flashMessage: string | null
  nodes: ChecklistItem[]
  connectorPaths: Array<{
    id: string
    path: string
    className?: string
    strokeWidth?: number
    opacity?: number
    stroke?: string
    sourceId: string
    targetId: string
  }>
  nodesById: Map<string, ChecklistItem>
  showChecklistWireHover: (element: Element, source: ChecklistItem, target: ChecklistItem) => void
  hideChecklistHover: () => void
  breakingWires: Array<{ id: string; path: string }>
  wireDraftPath: string | null
  displayNodes: ChecklistItem[]
  lockedNodeIds: Set<string>
  getExternalLock: (id: string) => { lockedByName: string } | null
  elementById: Map<string, TimelineElement>
  getRenderedNode: (node: ChecklistItem) => { position: { x: number; y: number } }
  canEditCanvas: boolean
  boardDocumentIdsRef: MutableRefObject<Set<string>>
  canDeleteChecklist: boolean
  handleNodePointerDown: (event: ReactPointerEvent<HTMLElement>, node: ChecklistItem) => void
  handleAnchorPointerDown: (event: ReactPointerEvent<HTMLElement>, node: ChecklistItem, anchor: 'top' | 'bottom') => void
  deleteGateRequest?: (node: ChecklistItem) => void
  deleteOpenGate?: (node: ChecklistItem) => void
  getNodeOperationalAccess: (node: ChecklistItem) => { hasAssignee: boolean }
  workspaceMembers: ChecklistMemberOption[]
  nodeParentPresentation: Map<string, { path: string; color: string }>
  handleBoardNodeMouseEnter: (event: ReactMouseEvent<HTMLElement>, node: ChecklistItem, parentPath: string, parentColor: string) => void
  handleBoardNodeMouseMove: (event: ReactMouseEvent<HTMLElement>, node: ChecklistItem) => void
  clearBoardNodeTooltip: () => void
  handleBoardNodeTouchStart: (event: ReactTouchEvent<HTMLElement>, node: ChecklistItem, parentPath: string, parentColor: string) => void
  resourceReadiness: { getSignalsForNode: (node: ChecklistItem) => { signals: any[] } }
  isGestureKnifeActive: boolean
  sliceableWires: SliceableWire[]
  sliceDependencyWires: (wireIds: string[]) => void | Promise<void>
}

export function BoardViewport({
  boardRef,
  isPlacingNode,
  viewportSize,
  handleBoardPointerDown,
  handlePointerMove,
  handlePointerUp,
  syncCanvasTransformFromScroll,
  canEditStructure,
  handleCalendarDrop,
  boardMetrics,
  boardScale,
  flashMessage,
  nodes,
  connectorPaths,
  nodesById,
  showChecklistWireHover,
  hideChecklistHover,
  breakingWires,
  wireDraftPath,
  displayNodes,
  lockedNodeIds,
  getExternalLock,
  elementById,
  getRenderedNode,
  canEditCanvas,
  boardDocumentIdsRef,
  canDeleteChecklist,
  handleNodePointerDown,
  handleAnchorPointerDown,
  deleteGateRequest,
  deleteOpenGate,
  getNodeOperationalAccess,
  workspaceMembers,
  nodeParentPresentation,
  handleBoardNodeMouseEnter,
  handleBoardNodeMouseMove,
  clearBoardNodeTooltip,
  handleBoardNodeTouchStart,
  resourceReadiness,
  isGestureKnifeActive,
  sliceableWires,
  sliceDependencyWires,
}: BoardViewportProps) {
  return (
    <div
      ref={boardRef}
      className={`checklistBoardCanvas ${isPlacingNode ? 'isPlacing' : ''}`}
      data-orbit-target="canvas"
      style={{
        // Always fill the parent. The measured `viewportSize.width` (kept in
        // sync by the ResizeObserver) drives only the inner world + minimap.
        width: '99.5%',
        height: `${viewportSize.height}px`,
        // Pan and pinch are both JS-driven so we get uniform momentum and
        // pinch behavior across platforms. `touch-action: none` prevents iOS
        // from layering its own native scroll-momentum on top of ours.
        touchAction: 'none',
      }}
      onPointerDown={handleBoardPointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onScroll={syncCanvasTransformFromScroll}
      onDragOver={(event) => {
        if (!canEditStructure) return
        event.preventDefault()
        event.dataTransfer.dropEffect = 'copy'
      }}
      onDrop={handleCalendarDrop}
    >
      {/* The ruled-grid background and the world both sit inside this
       * scroll-extent wrapper, whose pixel dimensions are the world size
       * MULTIPLIED by boardScale. Their inner content keeps its unscaled
       * layout coordinates and is visually scaled via `transform: scale()`
       * with origin (0,0), so scrollbars track the actual painted content. */}
      <div
        className="checklistBoardScaleHost"
        style={{
          width: boardMetrics.width * boardScale,
          height: boardMetrics.height * boardScale,
        }}
      >
        <div
          className="checklistBoardScaleStage"
          style={{
            width: boardMetrics.width,
            height: boardMetrics.height,
            transform: `scale(${boardScale})`,
          }}
        >
          <div
            className="checklistBoardRuled"
            style={{ width: boardMetrics.width, height: boardMetrics.height }}
          />
          {flashMessage && <div className="checklistBoardFlash">{flashMessage}</div>}
          {nodes.length === 0 && (
            <div className="checklistBoardEmpty">
              No board nodes yet.
            </div>
          )}
          <div
            className="checklistBoardWorld"
            style={{
              width: boardMetrics.width,
              height: boardMetrics.height,
            }}
          >
            <svg
              className="checklistBoardWires"
              width={boardMetrics.width}
              height={boardMetrics.height}
              viewBox={`0 0 ${boardMetrics.width} ${boardMetrics.height}`}
            >
              <defs>
                <clipPath id="checklist-wire-world-clip">
                  <rect x="0" y="0" width={boardMetrics.width} height={boardMetrics.height} />
                </clipPath>
                <marker id="checklist-arrow" markerWidth="10" markerHeight="10" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L0,6 L8,3 z" style={{ fill: 'var(--muted)' }} />
                </marker>
              </defs>
              <g clipPath="url(#checklist-wire-world-clip)">
                {connectorPaths.map((connector) => (
                  <path
                    key={connector.id}
                    d={connector.path}
                    className={connector.className}
                    style={{
                      strokeWidth: connector.strokeWidth,
                      opacity: connector.opacity,
                      ...(connector.stroke ? { stroke: connector.stroke } : {}),
                    }}
                    markerEnd="url(#checklist-arrow)"
                    onMouseEnter={(event) => {
                      const source = nodesById.get(connector.sourceId)
                      const target = nodesById.get(connector.targetId)
                      if (!source || !target) return
                      showChecklistWireHover(event.currentTarget, source, target)
                    }}
                    onMouseLeave={() => hideChecklistHover()}
                  />
                ))}
                {breakingWires.map((connector) => (
                  <path
                    key={connector.id}
                    d={connector.path}
                    className="checklistBoardWireBreaking"
                  />
                ))}
                {wireDraftPath && <path d={wireDraftPath} className="checklistBoardWireDraft" />}
              </g>
            </svg>

            {displayNodes.map((node) => {
              const isLocked = lockedNodeIds.has(node.id)
              const externalLock = getExternalLock(node.id) ?? getExternalLock(node.parentContainerId)
              const externalBlockerId = node.blockedByExternalId ?? node.blockedByExternal?.targetId ?? null
              const externalBlocker = externalBlockerId ? (elementById.get(externalBlockerId) ?? null) : null
              const renderedNode = getRenderedNode(node)
              const isExpired = isTimerExpired(node)
              const isGate = isGateNode(node)
              const gateInputs = node.gateInputIds ?? []
              const isGateAnd = node.nodeKind === 'GATE_AND'
              const resourceSignals = resourceReadiness.getSignalsForNode(node).signals

              if (isGate) {
                const hasOutput = displayNodes.some((candidate) => candidate.prerequisites.includes(node.id))
                const isGateOpen = !(gateInputs.length >= 2 && gateInputs.length <= 4 && hasOutput)
                return (
                  <GateNode
                    key={node.id}
                    node={node}
                    x={renderedNode.position.x}
                    y={renderedNode.position.y}
                    diameter={GATE_DIAMETER}
                    gateInputCount={gateInputs.length}
                    isGateAnd={isGateAnd}
                    isGateOpen={isGateOpen}
                    canEditStructure={canEditCanvas && (!boardDocumentIdsRef.current.has(node.id) || canDeleteChecklist)}
                    onNodePointerDown={handleNodePointerDown}
                    onAnchorPointerDown={handleAnchorPointerDown}
                    onDeleteGate={deleteGateRequest}
                    onDeleteOpenGate={deleteOpenGate}
                  />
                )
              }

              const assignmentAccess = getNodeOperationalAccess(node)
              const assignee = node.assigneeId
                ? workspaceMembers.find((member) => member.userId === node.assigneeId) ?? null
                : null
              const assigneeName = node.assigneeName ?? assignee?.displayName ?? 'Assigned teammate'
              const assigneeRole = assignee?.role ? assignee.role.toLocaleLowerCase() : 'member'
              const parentPresentation = nodeParentPresentation.get(node.id)

              return (
                <BoardNode
                  key={node.id}
                  node={node}
                  x={renderedNode.position.x}
                  y={renderedNode.position.y}
                  width={NODE_WIDTH}
                  minHeight={NODE_BODY_HEIGHT}
                  backgroundColor={parentPresentation?.color ?? 'var(--chart-8)'}
                  isLocked={isLocked}
                  isExpired={isExpired}
                  externalLock={externalLock}
                  externalBlocker={externalBlocker}
                  assigneeName={assigneeName}
                  assigneeRole={assigneeRole}
                  hasAssignee={assignmentAccess.hasAssignee}
                  resourceSignals={resourceSignals}
                  parentPath={parentPresentation?.path ?? 'Dashboard'}
                  parentColor={parentPresentation?.color ?? 'var(--chart-8)'}
                  onNodePointerDown={handleNodePointerDown}
                  onAnchorPointerDown={handleAnchorPointerDown}
                  onMouseEnterNode={handleBoardNodeMouseEnter}
                  onMouseMoveNode={handleBoardNodeMouseMove}
                  onMouseLeaveNode={clearBoardNodeTooltip}
                  onTouchStartNode={handleBoardNodeTouchStart}
                  onTouchEndNode={clearBoardNodeTooltip}
                />
              )
            })}
          </div>
          {/* Teammate cursors removed for now (no real-time presence on the
           * checklist canvas; will return in a future release). */}
        </div>
      </div>
      {/* The wire knife canvas must overlay the VIEWPORT, not the scaled
       * world. Keeping it inside `checklistBoardScaleStage` (which has
       * `transform: scale(boardScale)`) caused the trail to draw far away
       * from the cursor at any zoom != 1, because the canvas's CSS box was
       * sized in post-transform pixels and then re-scaled by the parent
       * transform. As a direct child of `boardRef` (overflow:auto,
       * position:relative), `position:absolute; inset:0` sizes the canvas
       * to the visible viewport and absolute positioning keeps it pinned
       * there while world content scrolls underneath. */}
      <VectorGestureKnife
        isActive={isGestureKnifeActive}
        wires={sliceableWires}
        onSlice={(wireIds) => void sliceDependencyWires(wireIds)}
      />
    </div>
  )
}
